/**
 * Centralized meeting permission logic.
 * Single source of truth for creator vs shared attendee role determination.
 */

export interface MeetingPermissions {
  /** True when the current user is the meeting creator */
  readonly isCreator: boolean;
  /** True when viewing a shared link as a non-creator */
  readonly isSharedViewer: boolean;
  /** True when viewing a shared link as the creator */
  readonly isSharedCreator: boolean;
  /** True when a shared link is present (meetingId + creator in URL) */
  readonly isSharedLink: boolean;
  /** True when the user should have read-only access (same as isSharedViewer) */
  readonly isReadOnly: boolean;
  /** True when the user can add/delete/reorder time slots */
  readonly canEditSlots: boolean;
}

/**
 * Derive all meeting permissions from the current user alias and the meeting creator alias.
 *
 * @param currentUserAlias - The logged-in user's alias
 * @param creatorAlias - The meeting creator's alias (from URL param or scheduling data), null if no meeting context
 * @param hasSharedLink - Whether the page was loaded via a shared meeting link (meetingId + creator in URL)
 */
export function getMeetingPermissions(currentUserAlias: string, creatorAlias: string | null, hasSharedLink: boolean): MeetingPermissions {
  const isCreator = !!currentUserAlias && !!creatorAlias && creatorAlias.toLowerCase() === currentUserAlias.toLowerCase();
  const isSharedCreator = hasSharedLink && isCreator;
  const isSharedViewer = hasSharedLink && !isCreator;

  return {
    isCreator,
    isSharedViewer,
    isSharedCreator,
    isSharedLink: hasSharedLink,
    isReadOnly: isSharedViewer,
    canEditSlots: isCreator || !hasSharedLink
  };
}
